// Zustand store for the active presentation. Driven by:
//   - WebSocket events from the proxy (real `present_story` tool calls or Path A fallback)
//   - The demo cycler (hardcoded storyboards for visual review)
//
// Scene advancement (Plan B step 5):
//   - Demo mode: Next button calls advance() directly.
//   - Embed mode: voice_agent.html dispatches `concierge:audio_started` on
//     the first PCM delta after a storyboard arrives. App.tsx anchors a rAF
//     loop to that timestamp; on each frame it computes elapsed ms and
//     advances the index when elapsed crosses target_start_time.
//   - If audio never arrives within AUDIO_ANCHOR_FALLBACK_MS, App.tsx falls
//     back to wall-clock anchoring on the storyboard-dispatch timestamp so
//     scenes still advance (degraded but visible).

import { create } from 'zustand';
import type { Scene, Storyboard } from '../types';

interface PresentationState {
  storyboard: Storyboard | null;
  currentSceneIndex: number;
  isPlaying: boolean;
  /** performance.now() at the moment audio playback began for this storyboard. null until the first PCM delta arrives. */
  playbackStartedAt: number | null;
  /** performance.now() at the moment the storyboard was ingested — used as a fallback anchor if audio never arrives. */
  storyboardIngestedAt: number | null;
  ingest: (sb: Storyboard) => void;
  advance: () => void;
  setIndex: (i: number) => void;
  setPlaybackStart: (t: number) => void;
  interrupt: () => void;
  currentScene: () => Scene | null;
}

const MS_PER_WORD   = 380;   // empirical Grok TTS cadence; was 400, tightened slightly
const PRE_SCENE_PAD = 150;   // small breath between scenes
const MIN_SCENE_MS  = 1400;  // never flash a scene shorter than this

export const usePresentationStore = create<PresentationState>((set, get) => ({
  storyboard: null,
  currentSceneIndex: -1,
  isPlaying: false,
  playbackStartedAt: null,
  storyboardIngestedAt: null,

  ingest: (sb) => {
    let cursor = 0;
    const scenes = sb.scenes.map((s) => {
      const words = (s.spoken_script || '').trim().split(/\s+/).filter(Boolean).length;
      const dur = Math.max(words * MS_PER_WORD, MIN_SCENE_MS);
      const start = cursor + PRE_SCENE_PAD;
      const end = start + dur;
      cursor = end;
      return { ...s, target_start_time: start, target_end_time: end };
    });
    set({
      storyboard: { ...sb, scenes },
      currentSceneIndex: 0,
      isPlaying: true,
      playbackStartedAt: null,
      storyboardIngestedAt: performance.now(),
    });
  },

  advance: () => {
    const { storyboard, currentSceneIndex } = get();
    if (!storyboard) return;
    if (currentSceneIndex < storyboard.scenes.length - 1) {
      set({ currentSceneIndex: currentSceneIndex + 1 });
    } else {
      set({ isPlaying: false });
    }
  },

  setIndex: (i) => set({ currentSceneIndex: i }),

  setPlaybackStart: (t) => set({ playbackStartedAt: t }),

  interrupt: () =>
    set({
      storyboard: null,
      currentSceneIndex: -1,
      isPlaying: false,
      playbackStartedAt: null,
      storyboardIngestedAt: null,
    }),

  currentScene: () => {
    const { storyboard, currentSceneIndex } = get();
    if (!storyboard || currentSceneIndex < 0) return null;
    return storyboard.scenes[currentSceneIndex] ?? null;
  },
}));
